// Minimal changes for Vite compatibility

import {
  ArrowTriangleIcon,
  CurecallDropdownButton,
  CurecallDropdownItemStyle,
  CurecallList,
  ECurecallDropdownPosition,
  ECurecallSize,
} from '@honestica/curecall-ui'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { ProjectService } from '../../../services/project.service'
import { LocalizedLink } from '../../../components/navigation/localized-link.component'
import { useLocalizedNavigate } from '../../../hooks/use-localized-navigate'
import { useQuery } from '@tanstack/react-query'
import type { TProject } from '../../../types/project'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../../hooks/use-auth'
import { twMerge } from 'src/utils/tw-merge.util'
import { getRedirectAfterLoginPath } from 'src/providers/auth.provider'

const BackToLoginPageButton = () => {
  const { t } = useTranslation()
  const { logout } = useAuth()

  const handleSignOut = () => {
    logout && logout()
  }

  return (
    <button className="c-flex c-gap-2 c-items-center c-text-primary c-font-semibold" onClick={handleSignOut}>
      <ArrowTriangleIcon className="c-h-4 c-w-4 c-rotate-[-90deg]" position="LEFT" />
      <span>{t('pages.auth.cta.back-to-connexion-page')}</span>
    </button>
  )
}

export const SelectProjectForm = () => {
  const navigate = useLocalizedNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const {
    isLoading,
    error,
    data: projectList,
  } = useQuery({
    queryKey: ['project-list'],
    queryFn: () => ProjectService.getProjectsList(),
  })
  const numberOfProjects = projectList?.length

  useEffect(() => {
    if (numberOfProjects === 1 && projectList) {
      const redirectPath = getRedirectAfterLoginPath(location.search)
      navigate(redirectPath ?? `/${projectList[0]?.id}`)
    }
  }, [numberOfProjects, projectList, navigate, location.search])

  if (error) return <ErrorQueryHandler error={error} />

  if (numberOfProjects === 0)
    return (
      <div className="c-flex c-flex-col c-gap-20">
        <BackToLoginPageButton />
        <h2>{t('pages.auth.description.select-project-no-projects')}</h2>
      </div>
    )

  const tableRowsData =
    projectList?.map((project: any, index: any) => ({
      ...project,
      name: {
        component: <RowLink project={project} />,
        observedData: project.name,
      },
      actions: (
        <RowDropdown
          projectId={project.id}
          menuPosition={
            index < projectList?.length - 1
              ? ECurecallDropdownPosition['BOTTOM_LEFT']
              : ECurecallDropdownPosition['TOP_LEFT']
          }
        />
      ),
    })) || []

  return (
    <div className="c-flex c-flex-col c-gap-8 c-max-h-[calc(100vh-100px)]">
      <BackToLoginPageButton />
      <h2>{t('pages.auth.title.select-project')}</h2>
      {!error && tableRowsData && (
        <CurecallList
          rowsData={tableRowsData}
          defaultSortData={{ key: 'id', descOrder: false }}
          isLoading={isLoading}
          loadingLabel={t('pages.auth.wording.loading')}
          headersData={[
            { label: '#', key: 'id', isSorted: true },
            {
              label: t('pages.auth.wording.project-name'),
              key: 'name',
              isSorted: true,
            },
            { label: t('pages.auth.wording.sms-sender'), key: 'smsSender' },
            { label: t('pages.auth.wording.actions'), key: 'actions' },
          ]}
        />
      )}
    </div>
  )
}

const RowLink = ({ project }: { project: Pick<TProject, 'id' | 'name'> }) => {
  return (
    <LocalizedLink className="c-font-bold c-text-primary c-cursor-pointer" to={`/${project.id}`}>
      {project.name}
    </LocalizedLink>
  )
}

const RowDropdown = ({
  projectId,
  menuPosition,
}: {
  projectId: number
  menuPosition: ECurecallDropdownPosition
}) => {
  const { t } = useTranslation()

  return (
    <CurecallDropdownButton
      dropdownButtonLabel={t('pages.auth.wording.actions')}
      size={ECurecallSize.SMALL}
      position={menuPosition}
      customItems={[
        <li key={`patients-link-project-${projectId}`}>
          <LocalizedLink
            className={twMerge(CurecallDropdownItemStyle, 'hover:c-text-grey-700')}
            to={`/${projectId}/medical/patients`}
          >
            {t('pages.auth.wording.patients-link')}
          </LocalizedLink>
        </li>,
        <li key={`users-link-project-${projectId}`}>
          <LocalizedLink
            className={twMerge(CurecallDropdownItemStyle, 'hover:c-text-grey-700')}
            to={`/${projectId}/medical/users`}
          >
            {t('pages.auth.wording.users-link')}
          </LocalizedLink>
        </li>,
      ]}
    />
  )
}
